import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  Activity,
  AlertTriangle,
  Bot,
  CheckCircle,
  CheckCircle2,
  Clock3,
  ExternalLink,
  RefreshCw,
  ShieldCheck,
  Wifi,
  WifiOff,
} from 'lucide-react';
import './Errors.css';
import PageHeader from '../components/PageHeader';
import { datumUndZeit } from '../lib/zeit';

const LEVEL_COLOR = { fatal: '#7f1d1d', error: '#dc2626', warning: '#b7791f', info: '#4b5563', debug: '#4b5563' };

// Sentry-Projekt-Slug + Release → erkennbare Quelle (Badge). Matcht per Teilstring,
// damit beliebige Projektnamen (voogo-ios, voogo-ios-kunde, …) UND der Release-Tag
// (voogo-kunde@ / voogo-fahrer@) greifen — so wird Kunde/Fahrer im selben Projekt getrennt.
function sourceOf(it) {
  const s = String((it.project || '') + ' ' + (it.release || '')).toLowerCase();
  const role = /kunde|customer|rider|passenger/.test(s) ? ' Kunde'
             : /fahrer|driver/.test(s) ? ' Fahrer' : '';
  if (s.includes('ios'))     return { key: 'ios',     label: '📱 iOS' + (role || '-App'),     color: '#1d4ed8', bg: '#dbeafe' };
  if (s.includes('android')) return { key: 'android', label: '🤖 Android' + (role || '-App'), color: '#047857', bg: '#d1fae5' };
  if (s.includes('web') || s.includes('admin') || s.includes('partner')) return { key: 'web', label: '🌐 Web', color: '#6d28d9', bg: '#ede9fe' };
  if (s.includes('backend') || s.includes('node') || s.includes('server') || s.includes('api')) return { key: 'backend', label: '⚙️ Backend', color: '#374151', bg: '#f3f4f6' };
  return { key: 'other', label: it.project || 'Unbekannt', color: '#4b5563', bg: '#f3f4f6' };
}

const FILTERS = [
  { key: 'all',     label: 'Alle' },
  { key: 'apps',    label: '📱 Apps (iOS + Android)' },
  { key: 'ios',     label: 'iOS' },
  { key: 'android', label: 'Android' },
  { key: 'backend', label: '⚙️ Backend' },
  { key: 'web',     label: '🌐 Web' },
];
const inFilter = (it, f) => {
  const k = sourceOf(it).key;
  if (f === 'all') return true;
  if (f === 'apps') return k === 'ios' || k === 'android';
  return k === f;
};

const fmt = (s) => datumUndZeit(s, { jahr: false, leer: '' });

const levelLabel = (level) => ({
  fatal: 'Fatal',
  error: 'Fehler',
  warning: 'Warnung',
  info: 'Info',
  debug: 'Debug',
}[level] || level || 'Fehler');

export default function Errors() {
  const [issues, setIssues] = useState([]);
  const [open, setOpen] = useState(0);
  const [configured, setConfigured] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = useCallback(() => {
    api.get('/sentry/issues')
      .then(r => { setIssues(r.data.issues || []); setOpen(r.data.open || 0); setConfigured(r.data.configured); })
      .catch(() => {});
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const ack = async (id) => {
    try { await api.patch(`/sentry/issues/${id}/ack`); load(); }
    catch { toast.error('Fehler beim Markieren'); }
  };

  const shown = issues.filter(it => inFilter(it, filter));
  const countFor = (f) => issues.filter(it => inFilter(it, f)).length;
  const loadedOpen = issues.filter(it => it.status !== 'resolved').length;

  return (
    <div className="errors-page">
      <PageHeader
        title="Fehler-Monitoring"
        description="Fehler aus Backend, Web und Apps zentral einordnen, prüfen und für die weitere Analyse vorbereiten."
        actions={
        <div className="errors-hero-actions">
          <Link to="/agent" className="errors-agent-link" title="Der Bug-Analyst macht aus diesen Fehlern fertige Fix-Aufträge für deinen Entwickler">
            <Bot size={16} /> Bug-Analyse
          </Link>
          <button type="button" onClick={load} className="errors-refresh">
            <RefreshCw size={16} /> Aktualisieren
          </button>
        </div>
      }
      />

      <section className={`errors-connection ${configured ? 'is-connected' : 'is-disconnected'}`}>
        <div className="errors-connection-icon">
          {configured ? <Wifi size={22} /> : <WifiOff size={22} />}
        </div>
        <div>
          <strong>{configured ? 'Sentry-Integration verbunden' : 'Sentry-Webhook noch nicht verbunden'}</strong>
          <p>
            {configured
              ? 'Neue Incidents werden automatisch in diese systemweite Inbox übernommen.'
              : <>Richte eine Sentry „Internal Integration“ mit <code>/api/sentry/webhook</code> ein und setze <code>SENTRY_WEBHOOK_SECRET</code>. Backend, Web und Apps laufen in dieselbe Ansicht.</>}
          </p>
        </div>
        <span>{configured ? 'Verbunden' : 'Einrichtung offen'}</span>
      </section>

      <section className="errors-metrics" aria-label="Incident-Übersicht">
        <article className={open > 0 ? 'is-danger' : ''}>
          <div className="errors-metric-icon"><AlertTriangle size={20} /></div>
          <div><span>Systemweit offen</span><strong>{open}</strong><small>Backend-Zähler</small></div>
        </article>
        <article>
          <div className="errors-metric-icon"><Activity size={20} /></div>
          <div><span>Geladen</span><strong>{issues.length}</strong><small>letzte Incidents</small></div>
        </article>
        <article>
          <div className="errors-metric-icon"><Clock3 size={20} /></div>
          <div><span>Davon offen</span><strong>{loadedOpen}</strong><small>in dieser Ergebnismenge</small></div>
        </article>
        <article>
          <div className="errors-metric-icon"><Wifi size={20} /></div>
          <div><span>App-Quellen</span><strong>{countFor('apps')}</strong><small>iOS und Android</small></div>
        </article>
      </section>

      <section className="errors-section">
        <div className="errors-section-heading">
          <div className="errors-heading-index">01</div>
          <div>
            <span>Quellen</span>
            <h2>Incident Inbox</h2>
            <p>Die Filter wirken ausschließlich auf die aktuell geladenen Sentry-Ereignisse.</p>
          </div>
          <div className="errors-polling-pill"><RefreshCw size={14} /> Automatisch · 30 s</div>
        </div>

        <div className="errors-filter-row" aria-label="Incident-Quellen">
          {FILTERS.map(item => {
            const active = filter === item.key;
            const count = countFor(item.key);
            return (
              <button
                type="button"
                key={item.key}
                data-filter={item.key}
                className={active ? 'is-active' : ''}
                onClick={() => setFilter(item.key)}
                aria-pressed={active}
              >
                <span>{item.label}</span>
                <strong>{count}</strong>
              </button>
            );
          })}
        </div>

        <div className="errors-list">
          <div className="errors-list-head">
            <span>Priorität &amp; Quelle</span>
            <span>Incident</span>
            <span>Aktivität</span>
            <span>Aktion</span>
          </div>
          {shown.length === 0 ? (
            <div className="errors-empty">
              <CheckCircle2 size={28} />
              <strong>{issues.length === 0 ? 'Keine Fehler' : 'Keine Fehler in diesem Filter'}</strong>
              <span>Für diese Auswahl liegen aktuell keine Incidents vor.</span>
            </div>
          ) : shown.map(it => {
            const src = sourceOf(it);
            const resolved = it.status === 'resolved';
            return (
              <article className={`errors-row ${resolved ? 'is-resolved' : ''}`} key={it.id} data-issue-id={it.id}>
                <div className="errors-source-cell">
                  <span className="errors-level-dot" style={{ background: LEVEL_COLOR[it.level] || '#dc2626' }} title={it.level} />
                  <div>
                    <span className="errors-level-label">{levelLabel(it.level)}</span>
                    <span className="errors-source-badge" style={{ color: src.color, background: src.bg }} title={it.project || ''}>
                      {src.label}
                    </span>
                  </div>
                </div>

                <div className="errors-incident-cell">
                  <strong>{it.title}</strong>
                  <span>{it.environment || 'Umgebung unbekannt'}{it.culprit ? ` · ${it.culprit}` : ''}</span>
                </div>

                <div className="errors-activity-cell">
                  <strong>{it.event_count ?? 0}×</strong>
                  <span>{fmt(it.last_seen || it.received_at) || 'Zeit unbekannt'}</span>
                </div>

                <div className="errors-actions-cell">
                  <span className={`errors-state ${resolved ? 'is-resolved' : ''}`}>{resolved ? 'Lokal erledigt' : 'Offen'}</span>
                  {it.permalink && (
                    <a href={it.permalink} target="_blank" rel="noreferrer" title="In Sentry öffnen" aria-label={`${it.title} in Sentry öffnen`}>
                      <ExternalLink size={16} />
                    </a>
                  )}
                  {!resolved && (
                    <button type="button" onClick={() => ack(it.id)} title="Als erledigt markieren" aria-label={`${it.title} lokal als erledigt markieren`}>
                      <CheckCircle size={17} />
                      <span>Lokal erledigt</span>
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <footer className="errors-boundary">
        <CheckCircle size={19} />
        <div>
          <strong>Lokale Cockpit-Markierung</strong>
          <p>„Lokal erledigt“ ändert ausschließlich den VOOGO-Eintrag. Der Incident wird dadurch nicht in Sentry aufgelöst.</p>
        </div>
        <span>Keine Sentry-Mutation</span>
      </footer>
    </div>
  );
}
